import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth as useSupabaseAuth } from '@/hooks/useAuth';
import type { User } from '@/hooks/useAuth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, userData: { firstName: string; lastName: string; role?: string }) => Promise<{ success: boolean; error?: string }>;
  signOut: (options?: { redirectTo?: string }) => Promise<{ success: boolean; error?: string }>;
  checkUser: (forceCheck?: boolean) => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Utiliser le hook useSupabaseAuth sans redirection intégrée
  const { 
    user, 
    loading, 
    error, 
    isAuthenticated, 
    signIn, 
    signUp, 
    checkUser: supabaseCheckUser, 
    signOut: supabaseSignOut 
  } = useSupabaseAuth();
  
  const [initialized, setInitialized] = useState(false);

  // Envelopper checkUser pour gérer l'état d'initialisation
  const checkUser = useCallback(async (forceCheck = false) => {
    const user = await supabaseCheckUser(forceCheck);
    if (!initialized) {
      setInitialized(true);
    }
    return user;
  }, [initialized, supabaseCheckUser]);

  // Envelopper signOut pour gérer la redirection dans les composants
  const signOut = useCallback(async (options?: { redirectTo?: string }) => {
    return await supabaseSignOut(options);
  }, [supabaseSignOut]);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    if (!initialized && !loading) {
      checkUser(true);
    }
  }, [initialized, loading, checkUser]);

  const value = {
    user,
    loading: loading || !initialized,
    error,
    isAuthenticated: initialized ? isAuthenticated : false,
    signIn,
    signUp,
    signOut,
    checkUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
